import React from "react";
import { Button, Form, Input, Modal } from "antd";
import { useNavigate, Link } from "react-router-dom";
import styled from "styled-components";
import httpClient from "../axios";
import { notifyError, notifySuccess } from "../helper";
import { isLoggedIn, saveLogin } from "../lib/auth";
import { ReactComponent as VrtsLogo } from "../Assets/svg/VRTS_Logo.svg";
import background from "../Assets/images/login_screen.png";

export const Split = styled.div`
	min-height: 100vh;
	display: grid;
	grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr);
	@media (max-width: 900px) {
		grid-template-columns: 1fr;
	}
`;

export const Hero = styled.div`
	background: var(--lcds-navy) url(${background}) center / cover no-repeat;
	position: relative;
	display: flex;
	align-items: flex-end;
	padding: 48px;
	color: #fff;
	&::after {
		content: "";
		position: absolute;
		inset: 0;
		background: linear-gradient(180deg, rgba(42, 56, 64, 0.35) 0%, rgba(42, 56, 64, 0.9) 100%);
	}
	> div {
		position: relative;
		z-index: 1;
		max-width: 520px;
	}
	svg {
		height: 80px;
		width: auto;
		margin-bottom: 24px;
	}
	h1 {
		color: #fff;
		font-size: 34px;
		font-weight: 700;
		margin: 0 0 12px;
	}
	p {
		color: rgba(255, 255, 255, 0.85);
		font-size: 16px;
		margin: 0;
	}
	@media (max-width: 900px) {
		min-height: 220px;
		padding: 32px 24px;
		svg {
			height: 56px;
		}
		h1 {
			font-size: 26px;
		}
	}
`;

export const FormPane = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 48px 24px;
	background: var(--lcds-bg);
`;

export const FormBox = styled.div`
	width: 100%;
	max-width: 400px;
	background: #fff;
	border: 1px solid var(--lcds-border);
	border-radius: 12px;
	padding: 36px 32px;
	box-shadow: 0 6px 24px rgba(31, 42, 48, 0.08);
	h2 {
		margin: 0 0 4px;
		font-size: 24px;
		color: var(--lcds-navy);
	}
	.hint {
		color: var(--lcds-muted);
		margin-bottom: 24px;
	}
	.ant-input,
	.ant-input-affix-wrapper {
		border-radius: 6px;
	}
	.ant-input-affix-wrapper > .ant-input {
		border-radius: 0;
	}
	.ant-btn {
		border-radius: 6px;
		height: 44px;
		font-weight: 600;
	}
`;

const Login = () => {
	const navigate = useNavigate();
	const http = React.useMemo(() => httpClient(), []);
	const [loading, setLoading] = React.useState(false);
	const [forgotOpen, setForgotOpen] = React.useState(false);
	const [forgotLoading, setForgotLoading] = React.useState(false);

	React.useEffect(() => {
		if (isLoggedIn()) {
			navigate("/mode-selection", { replace: true });
		}
	}, [navigate]);

	const onFinish = (values) => {
		setLoading(true);
		http
			.post("/auth/login", { email: values.email.trim(), password: values.password })
			.then((response) => {
				saveLogin(response.data);
				navigate("/mode-selection");
			})
			.catch((error) => notifyError(error, "Invalid email or password"))
			.finally(() => setLoading(false));
	};

	const onForgot = (values) => {
		setForgotLoading(true);
		http
			.post("/auth/forgot-password", { email: values.email.trim() })
			.then((response) => {
				notifySuccess("Check your inbox", response.data.message);
				setForgotOpen(false);
			})
			.catch((error) => notifyError(error, "Could not send the reset email"))
			.finally(() => setForgotLoading(false));
	};

	return (
		<Split>
			<Hero>
				<div>
					<VrtsLogo />
					<h1>LCDS Training admin</h1>
					<p>Manage contacts, events and VR training results for the LCDS scenarios.</p>
				</div>
			</Hero>
			<FormPane>
				<FormBox>
					<h2>Sign in</h2>
					<div className="hint">Use your administrator email and password.</div>
					<Form layout="vertical" onFinish={onFinish} requiredMark={false} size="large">
						<Form.Item
							label="Email"
							name="email"
							rules={[{ required: true, type: "email", message: "Enter your email address" }]}
						>
							<Input placeholder="you@example.com" autoComplete="username" />
						</Form.Item>
						<Form.Item
							label="Password"
							name="password"
							rules={[{ required: true, message: "Enter your password" }]}
						>
							<Input.Password placeholder="Password" autoComplete="current-password" />
						</Form.Item>
						<Button type="primary" htmlType="submit" block loading={loading}>
							Sign in
						</Button>
					</Form>
					<div style={{ marginTop: 16, textAlign: "center" }}>
						<Button type="link" onClick={() => setForgotOpen(true)}>
							Forgot your password?
						</Button>
					</div>
				</FormBox>
			</FormPane>

			<Modal
				title="Reset your password"
				visible={forgotOpen}
				onCancel={() => setForgotOpen(false)}
				footer={null}
				destroyOnClose
			>
				<p style={{ color: "var(--lcds-muted)" }}>
					Enter your account email and we will send you a link to choose a new password.
				</p>
				<Form layout="vertical" onFinish={onForgot} requiredMark={false} size="large">
					<Form.Item
						label="Email"
						name="email"
						rules={[{ required: true, type: "email", message: "Enter your email address" }]}
					>
						<Input placeholder="you@example.com" />
					</Form.Item>
					<Button type="primary" htmlType="submit" block loading={forgotLoading}>
						Send reset link
					</Button>
				</Form>
				<p style={{ marginTop: 16, marginBottom: 0, fontSize: 12, color: "var(--lcds-muted)" }}>
					Already have a reset link? <Link to="/reset-password">Open the reset page</Link>.
				</p>
			</Modal>
		</Split>
	);
};

export default Login;
